import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Plus, Edit } from 'lucide-react';
import { createComponentLogger } from '@/utils/logger';
import { partnerContactService, type PartnerContact } from '@/services/partnerContactService';

const log = createComponentLogger('QuickAddPartnerModal', 'contacts');

const HONORIFIC_OPTIONS = [
  { value: 'Dr.', label: 'Dr.' },
  { value: 'Mr.', label: 'Mr.' },
  { value: 'Mrs.', label: 'Mrs.' },
  { value: 'Ms.', label: 'Ms.' },
  { value: 'Mx.', label: 'Mx.' },
  { value: 'Rev.', label: 'Rev.' },
  { value: 'Prof.', label: 'Prof.' }
];

interface QuickAddPartnerModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onPartnerCreated?: (partner: PartnerContact) => void;
  mode?: 'create' | 'edit';
  initialPartner?: PartnerContact | null;
  partnerId?: string;
}

type PartnerTypeOption = 'realtor' | 'cpa' | 'other';

interface PartnerFormData {
  partnerType: PartnerTypeOption | '';
  customPartnerType: string;
  salutation?: string;
  firstName: string;
  lastName: string;
  email: string;
  mobilePhone: string;
}

export function QuickAddPartnerModal({
  open,
  onClose,
  onSuccess,
  onPartnerCreated,
  mode = 'create',
  initialPartner = null,
  partnerId
}: QuickAddPartnerModalProps) {
  const [loading, setLoading] = useState(false);

  const [partner, setPartner] = useState<PartnerFormData>({
    partnerType: '',
    customPartnerType: '',
    salutation: '',
    firstName: '',
    lastName: '',
    email: '',
    mobilePhone: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load initial partner data when in edit mode
  useEffect(() => {
    if (mode === 'edit' && initialPartner && open) {
      log.info('Loading partner data for edit', { partnerId: initialPartner.id });

      // Determine if we need to use custom partner type
      const knownTypes = ['realtor', 'cpa'];
      const isKnownType = knownTypes.includes(initialPartner.partnerType);

      setPartner({
        partnerType: isKnownType ? (initialPartner.partnerType as PartnerTypeOption) : 'other',
        customPartnerType: isKnownType ? '' : initialPartner.partnerType,
        salutation: initialPartner.salutation || '',
        firstName: initialPartner.firstName || '',
        lastName: initialPartner.lastName || '',
        email: initialPartner.email || '',
        mobilePhone: initialPartner.mobilePhone || ''
      });
    } else if (mode === 'create' && open) {
      // Reset form for create mode
      setPartner({
        partnerType: '',
        customPartnerType: '',
        salutation: '',
        firstName: '',
        lastName: '',
        email: '',
        mobilePhone: ''
      });
    }
  }, [mode, initialPartner, open]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!partner.partnerType) {
      newErrors.partnerType = 'Partner type is required';
    }

    if (partner.partnerType === 'other' && !partner.customPartnerType.trim()) {
      newErrors.customPartnerType = 'Please specify partner type';
    }

    if (!partner.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!partner.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    // At least one contact method required (email OR phone)
    const hasEmail = partner.email.trim().length > 0;
    const hasPhone = partner.mobilePhone.trim().length > 0;

    if (!hasEmail && !hasPhone) {
      newErrors.email = 'Email or cell phone is required';
      newErrors.mobilePhone = 'Email or cell phone is required';
    } else {
      // If email is provided, validate format
      if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(partner.email)) {
        newErrors.email = 'Please enter a valid email';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);

    try {
      // Check for duplicate email
      if (partner.email.trim()) {
        const existingByEmail = await partnerContactService.checkEmailExists(
          partner.email.trim(),
          mode === 'edit' ? partnerId : undefined
        );
        if (existingByEmail) {
          toast.error(
            `A partner with email "${partner.email}" already exists. ` +
            `Partner name: ${existingByEmail.firstName} ${existingByEmail.lastName}.`
          );
          setLoading(false);
          return;
        }
      }

      // Check for duplicate phone
      if (partner.mobilePhone.trim()) {
        const existingByPhone = await partnerContactService.checkPhoneExists(
          partner.mobilePhone.trim(),
          mode === 'edit' ? partnerId : undefined
        );
        if (existingByPhone) {
          toast.error(
            `A partner with this phone number already exists. ` +
            `Partner name: ${existingByPhone.firstName} ${existingByPhone.lastName}.`
          );
          setLoading(false);
          return;
        }
      }

      // Determine the final partner type value
      const finalPartnerType = partner.partnerType === 'other'
        ? (partner.customPartnerType.trim() as PartnerContact['partnerType'])
        : (partner.partnerType as PartnerContact['partnerType']);

      if (mode === 'edit' && partnerId) {
        // Update existing partner
        log.info('Updating partner contact', {
          partnerId,
          partnerType: finalPartnerType,
          partnerName: `${partner.firstName} ${partner.lastName}`
        });

        await partnerContactService.update(partnerId, {
          partnerType: finalPartnerType,
          salutation: partner.salutation && partner.salutation.trim() ? partner.salutation.trim() : undefined,
          firstName: partner.firstName.trim(),
          lastName: partner.lastName.trim(),
          companyName: `${partner.firstName.trim()  } ${  partner.lastName.trim()}`,
          email: partner.email.trim() || undefined,
          mobilePhone: partner.mobilePhone.trim() || undefined
        });

        log.info('Partner updated successfully');
        toast.success('Partner updated successfully');
      } else {
        // Create new partner
        log.info('Creating partner contact', {
          partnerType: finalPartnerType,
          partnerName: `${partner.firstName} ${partner.lastName}`
        });

        const created = await partnerContactService.create({
          partnerType: finalPartnerType,
          salutation: partner.salutation && partner.salutation.trim() ? partner.salutation.trim() : undefined,
          firstName: partner.firstName.trim(),
          lastName: partner.lastName.trim(),
          companyName: `${partner.firstName.trim()  } ${  partner.lastName.trim()}`,
          email: partner.email.trim() || undefined,
          mobilePhone: partner.mobilePhone.trim() || undefined
        });

        log.info('Partner created successfully');
        toast.success('Partner added successfully');

        // Notify caller of the newly created partner (e.g., for auto-selection)
        onPartnerCreated?.(created);
      }

      // Reset form only in create mode
      if (mode === 'create') {
        setPartner({
          partnerType: '',
          customPartnerType: '',
          salutation: '',
          firstName: '',
          lastName: '',
          email: '',
          mobilePhone: ''
        });
      }
      setErrors({});

      // Call success callback to refresh list
      onSuccess();
      onClose();

    } catch (error) {
      log.error(`Failed to ${mode} partner`, { error });

      if (error instanceof Error && error.message.includes('already exists')) {
        toast.error('A partner with this email already exists');
      } else {
        toast.error(`Failed to ${mode === 'edit' ? 'update' : 'add'} partner. Please try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setPartner({
        partnerType: '',
        customPartnerType: '',
        salutation: '',
        firstName: '',
        lastName: '',
        email: '',
        mobilePhone: ''
      });
      setErrors({});
      onClose();
    }
  };

  const handlePartnerTypeChange = (value: string) => {
    setPartner({
      ...partner,
      partnerType: value as PartnerTypeOption,
      customPartnerType: value === 'other' ? partner.customPartnerType : ''
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'edit' ? <Edit className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
            {mode === 'edit' ? 'Edit Partner' : 'Quick Add Partner'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Partner Type Selection */}
          <div className="space-y-2">
            <Label htmlFor="partnerType">
              Partner Type <span className="text-red-500">*</span>
            </Label>
            <Select
              value={partner.partnerType}
              onValueChange={handlePartnerTypeChange}
              disabled={loading}
            >
              <SelectTrigger id="partnerType" className={errors.partnerType ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select partner type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="realtor">Realtor</SelectItem>
                <SelectItem value="cpa">CPA</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            {errors.partnerType && (
              <p className="text-xs text-red-500">{errors.partnerType}</p>
            )}
          </div>

          {/* Custom Partner Type (shown when "Other" is selected) */}
          {partner.partnerType === 'other' && (
            <div className="space-y-2">
              <Label htmlFor="customPartnerType">
                Specify Partner Type <span className="text-red-500">*</span>
              </Label>
              <Input
                id="customPartnerType"
                value={partner.customPartnerType}
                onChange={(e) => setPartner({ ...partner, customPartnerType: e.target.value })}
                placeholder="e.g., Financial Advisor, Attorney"
                disabled={loading}
                className={errors.customPartnerType ? 'border-red-500' : ''}
              />
              {errors.customPartnerType && (
                <p className="text-xs text-red-500">{errors.customPartnerType}</p>
              )}
            </div>
          )}

          {/* Title/Salutation */}
          <div className="space-y-2">
            <Label htmlFor="salutation">Title (Optional)</Label>
            <Select
              value={partner.salutation || ''}
              onValueChange={(value) => setPartner({ ...partner, salutation: value })}
              disabled={loading}
            >
              <SelectTrigger id="salutation">
                <SelectValue placeholder="Select title..." />
              </SelectTrigger>
              <SelectContent>
                {HONORIFIC_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="firstName">
                First Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="firstName"
                value={partner.firstName}
                onChange={(e) => setPartner({ ...partner, firstName: e.target.value })}
                placeholder="John"
                disabled={loading}
                className={errors.firstName ? 'border-red-500' : ''}
              />
              {errors.firstName && (
                <p className="text-xs text-red-500">{errors.firstName}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">
                Last Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="lastName"
                value={partner.lastName}
                onChange={(e) => setPartner({ ...partner, lastName: e.target.value })}
                placeholder="Doe"
                disabled={loading}
                className={errors.lastName ? 'border-red-500' : ''}
              />
              {errors.lastName && (
                <p className="text-xs text-red-500">{errors.lastName}</p>
              )}
            </div>
          </div>

          {/* Contact Information */}
          <p className="text-xs text-muted-foreground">* At least one contact method (email or phone) is required</p>

          <div className="space-y-2">
            <Label htmlFor="email">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={partner.email}
              onChange={(e) => setPartner({ ...partner, email: e.target.value })}
              placeholder="john.doe@example.com"
              disabled={loading}
              className={errors.email ? 'border-red-500' : ''}
            />
            {errors.email && (
              <p className="text-xs text-red-500">{errors.email}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="mobilePhone">
              Cell Phone
            </Label>
            <Input
              id="mobilePhone"
              type="tel"
              value={partner.mobilePhone}
              onChange={(e) => setPartner({ ...partner, mobilePhone: e.target.value })}
              placeholder="(555) 555-5555"
              disabled={loading}
              className={errors.mobilePhone ? 'border-red-500' : ''}
            />
            {errors.mobilePhone && (
              <p className="text-xs text-red-500">{errors.mobilePhone}</p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {mode === 'edit' ? 'Updating...' : 'Adding...'}
                </>
              ) : (
                <>
                  {mode === 'edit' ? <Edit className="mr-2 h-4 w-4" /> : <Plus className="mr-2 h-4 w-4" />}
                  {mode === 'edit' ? 'Update Partner' : 'Add Partner'}
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
